/**
 * 导出 **RenderTree** —— 从 Playground.vue 抽出来的。
 *
 * IR 里只有**功能数据**：`design` 整个键都不在里面，
 * 编辑器专用节点（框 / 注释）也被丢掉（连带的边一起丢）。
 *
 * 顺带把完整文档打一份到控制台 —— 这样能**对照着看**
 * 「设计器数据到底有没有漏出去」，而不是只能相信它没漏。
 */
import { toDocument, toRenderTree } from '@/graph/serialization'
import type { DocumentEdge, SourceNode } from '@/graph/serialization'
import type { EdgeView, NodeView } from './useSubgraphLevels'

export interface ExportCtx {
  nodeViews: () => NodeView[]
  edgeViews: () => EdgeView[]
  /** 这个类型是不是「编辑器专用」（框 / 注释）—— 领域知识由宿主给 */
  isEditorOnlyType: (type: string) => boolean
  /** 「这个节点类型来自哪个包」—— 文档里逐节点记的 `pkg`（见 `serialization.ts`） */
  packageOf: (type: string) => string | undefined
  showNotice: (message: string) => void
}

export function useExportIr(ctx: ExportCtx) {
  const { nodeViews, edgeViews, isEditorOnlyType, packageOf, showNotice } = ctx

function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * 导出 **RenderTree** —— 只有功能数据，`design` 整个键都不在里面。
 *
 * 同时把完整文档打一份到控制台：这样能**对照着看**
 * 「设计器数据到底有没有漏出去」，而不是只能相信它没漏。
 * （`verify:serialize` 有测试守着，但能肉眼对一次更放心。）
 *
 * 编辑器专用节点（框 / 注释）在这里被丢掉 —— 它们不产出任何东西。
 */
function exportRenderTree() {
  const doc = toDocument(
    nodeViews() as unknown as SourceNode[],
    edgeViews() as unknown as (DocumentEdge & Record<string, unknown>)[],
    { packageOf },
  )
  const ir = toRenderTree(doc, (node) => isEditorOnlyType(node.type))

  downloadJson(`rendertree-v${ir.version}.json`, ir)
  /*
   * ⚠️ 这里原来把**整份文档和整棵 RenderTree** `console.info` 出来。
   * 删掉了：导出的产物已经落成文件，提示条也报了数量 ——
   * 再往控制台灌两坨大对象，除了刷屏没有任何作用
   * （而且大对象在 devtools 里是懒展开的引用，排查时反而误导）。
   */
  showNotice(
    `已导出 RenderTree：${ir.nodes.length} 个节点 / ${ir.edges.length} 条边（丢掉了 ${doc.nodes.length - ir.nodes.length} 个编辑器专用节点）`,
  )
}

  return { exportRenderTree, downloadJson }
}
